import { API_BASE } from '../config';
import axios from 'axios';
import {
    BarElement,
    CategoryScale,
    Chart as ChartJS,
    Legend,
    LinearScale,
    Title,
    Tooltip,
} from 'chart.js';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import { useNavigate } from 'react-router-dom';
import './pages.css';
import './Result.css';
import { RatingOverview, QuestionNumbers, CommentKeywords } from './ResultAnalysis';

    ChartJS.register(
    BarElement,
    CategoryScale,
    LinearScale,
    Title,
    Tooltip,
    Legend
    );

    // === 공통 설정 ===

    const authAxios = axios.create({ baseURL: API_BASE });
    authAxios.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
    });

    const genderOptions = [
    { value: '', label: '전체' },
    { value: '남', label: '남' },
    { value: '여', label: '여' },
    ];

    const agePresets = [
    { value: '', label: '전체' },
    { value: '20-29', label: '20대' },
    { value: '30-39', label: '30대' },
    { value: '40-49', label: '40대' },
    { value: '50-59', label: '50대' },
    { value: '60-120', label: '60대 이상' },
    ];

    // === 데이터 훅 ===
    function useProgramsSitesSurveys(filters) {
    const { program_id, site_id } = filters;
    const [programs, setPrograms] = useState([]);
    const [sites, setSites] = useState([]);
    const [surveys, setSurveys] = useState([]);

    useEffect(() => {
        authAxios
        .get('/api/programs')
        .then(({ data }) => setPrograms(data || []))
        .catch(() => setPrograms([]));
    }, []);

    useEffect(() => {
        const controller = new AbortController(); setSites([]); setSurveys([]);
        if (program_id) authAxios.get('/api/sites', { params: { program_id }, signal: controller.signal })
            .then(({ data }) => { if (!controller.signal.aborted) setSites(data || []); }).catch(() => { if (!controller.signal.aborted) setSites([]); });
        return () => controller.abort();
    }, [program_id]);
    useEffect(() => {
        const controller = new AbortController(); setSurveys([]);
        if (site_id) authAxios.get('/api/surveys', { params: { site_id }, signal: controller.signal })
            .then(({ data }) => { if (!controller.signal.aborted) setSurveys(data || []); }).catch(() => { if (!controller.signal.aborted) setSurveys([]); });
        return () => controller.abort();
    }, [site_id]);

    return { programs, sites, surveys };
    }

    // === UI 컴포넌트 ===
    function KpiCards({ kpis, loading }) {
    const items = [
        { title: '배정 인원', value: kpis.assigned },
        { title: '시작/완료', value: `${kpis.started ?? '—'} / ${kpis.completed ?? '—'}` },
        {
        title: '완료율',
        value: kpis.assigned ? `${Math.round((kpis.completed / kpis.assigned) * 100)}%` : '-',
        },
    ];
    return (
        <div className="result-kpi-grid">
        {items.map((c, i) => (
            <div key={i} className="panel">
            <div className="muted">{c.title}</div>
            <div className="kpi">{loading ? '…' : c.value ?? '-'}</div>
            </div>
        ))}
        </div>
    );
    }

    function QuestionList({ ratingRows, objectiveRows, options }) {
    const groupedObj = useMemo(() => {
        const byQ = {};
        for (const r of objectiveRows) {
        if (!byQ[r.question_id]) byQ[r.question_id] = { question_text: r.question_text, site_name: r.site_name, survey_title: r.survey_title, items: [] };
        byQ[r.question_id].items.push({ option_text: r.option_text, cnt: r.cnt });
        }
        return byQ;
    }, [objectiveRows]);

    return (
        <div className="stack">
        {ratingRows.map((q) => (
            <div key={`r-${q.question_id}`} className="panel">
            <div className="panel-title">{q.question_text}</div><div className="sub-muted">{q.site_name} · {q.survey_title}</div>
            <div className="sub-muted">평균 {Math.round(q.avg_pct * 10) / 10}% · 응답 {q.response_count}건</div>
            <div className="chart-wrap">
                <Bar
                data={{
                    labels: (q.distribution || []).map((d) => `${d.score}`),
                    datasets: [{ label: '분포(건수)', data: (q.distribution || []).map((d) => d.cnt) }],
                }}
                options={options}
                />
            </div>
            </div>
        ))}

        {Object.entries(groupedObj).map(([qid, g]) => (
            <div key={`o-${qid}`} className="panel">
            <div className="panel-title">{g.question_text}</div><div className="sub-muted">{g.site_name} · {g.survey_title}</div>
            <div className="chart-wrap">
                <Bar
                data={{
                    labels: g.items.map((x) => x.option_text),
                    datasets: [{ label: '응답(건수)', data: g.items.map((x) => x.cnt) }],
                }}
                options={{ ...options, indexAxis: 'y' }}
                />
            </div>
            </div>
        ))}
        </div>
    );
    }

    function CommentsTab({ filters }) {
    const [q, setQ] = useState(''), [page, setPage] = useState(1), [result, setResult] = useState({ items: [], total: 0, keywords: [] });
    const [loading, setLoading] = useState(false), [error, setError] = useState(''), [retry, setRetry] = useState(0);
    useEffect(() => { setPage(1); }, [filters]);
    useEffect(() => {
        const controller = new AbortController();
        setLoading(true); setError('');
        authAxios.get('/api/results/comments-analysis', { params: { ...filters, q, page }, signal: controller.signal })
            .then(({ data }) => { if (!controller.signal.aborted) setResult({ items: data.items || [], total: data.total || 0, keywords: data.keywords || [] }); })
            .catch(() => { if (!controller.signal.aborted) { setResult({ items: [], total: 0, keywords: [] }); setError('코멘트를 불러오지 못했습니다. 다시 시도해 주세요.'); } })
            .finally(() => { if (!controller.signal.aborted) setLoading(false); });
        return () => controller.abort();
    }, [filters, q, page, retry]);
    return <div className="stack" aria-busy={loading}>
        <div className="row"><input value={q} onChange={event => { setQ(event.target.value); setPage(1); }} placeholder="키워드 검색" aria-label="코멘트 키워드 검색" className="input" /></div>
        {error && <p role="alert">{error} <button className="btn" onClick={() => setRetry(value => value + 1)}>다시 불러오기</button></p>}
        {loading ? <p>코멘트를 불러오는 중입니다.</p> : <><CommentKeywords sites={result.keywords} /><div className="sub-muted">코멘트 총 {result.total}건</div><div className="stack-8">{result.items.map(item => <div key={item.response_id} className="panel"><div className="sub-muted">{item.site_name} · {item.question_text}</div><div style={{ marginTop: 6, whiteSpace: 'pre-wrap' }}>{item.answer_text}</div><div className="muted tiny">{item.sex} · {item.age ?? '—'}세 · {new Date(item.submitted_at).toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' })}</div></div>)}</div></>}
        {result.total > 30 && <div className="pager"><button className="btn" disabled={loading || page <= 1} onClick={() => setPage(value => value - 1)}>이전</button><span>페이지 {page}</span><button className="btn" disabled={loading || page * 30 >= result.total} onClick={() => setPage(value => value + 1)}>다음</button></div>}
    </div>;
    }

    // === 페이지 ===
    export default function Result() {
    const navigate = useNavigate();

    const handleLogout = () => {
        localStorage.removeItem('token');
        navigate('/');
    };

    const [activeTab, setActiveTab] = useState('overview');
    const today = useMemo(() => new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Seoul', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date()), []);
    const [filters, setFilters] = useState({
        program_id: '',
        site_id: '',
        survey_id: '',
        gender: '',
        age: '',
        start_date: '',
        end_date: '',
    });

    const [exportState, setExportState] = useState('idle');
    const exportInFlight = useRef(false);

    const downloadExcel = async () => {
        // A ref also blocks clicks arriving before React renders the disabled state.
        if (exportInFlight.current) return;
        exportInFlight.current = true;
        setExportState('loading');
        let objectUrl;
        let anchor;
        try {
            const params = new URLSearchParams({
                program_id: filters.program_id || '',
                site_id: filters.site_id || '',
                survey_id: filters.survey_id || '',
                gender: filters.gender || '',
                ...(filters.age
                    ? { age_min: filters.age.split('-')[0], age_max: filters.age.split('-')[1] }
                    : {}),
                start_date: filters.start_date || '',
                end_date: filters.end_date || '',
            }).toString();
            const response = await fetch(`${API_BASE}/api/export/responses.xlsx?${params}`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
            });
            if (!response.ok) throw new Error('export_failed');
            const blob = await response.blob();
            objectUrl = URL.createObjectURL(blob);
            anchor = document.createElement('a');
            anchor.href = objectUrl;
            anchor.download = 'responses.xlsx';
            document.body.appendChild(anchor);
            anchor.click();
            setExportState('success');
        } catch {
            setExportState('error');
        } finally {
            anchor?.remove();
            // Allow the browser to start the download before releasing its blob URL.
            if (objectUrl) setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
            exportInFlight.current = false;
        }
    };

    const { programs, sites, surveys } = useProgramsSitesSurveys(filters);

    const [kpis, setKpis] = useState({
        assigned: 0,
        started: 0,
        completed: 0,
        responses: 0,
        responders: 0,
        avg_pct: null,
    });
    const [analysis, setAnalysis] = useState({ sites: [], overall: {}, ratingRows: [], objectiveRows: [] });
    const [error, setError] = useState(''), [retry, setRetry] = useState(0);
    const [ratingRows, setRatingRows] = useState([]);
    const [objectiveRows, setObjectiveRows] = useState([]);
    const [loading, setLoading] = useState(false);

    const apiParams = useMemo(() => {
        const p = {};
        if (filters.program_id) p.program_id = filters.program_id;
        if (filters.site_id) p.site_id = filters.site_id;
        if (filters.survey_id) p.survey_id = filters.survey_id;
        if (filters.gender) p.gender = filters.gender;
        if (filters.age) {
        const [min, max] = filters.age.split('-');
        p.age_min = min;
        p.age_max = max;
        }
        if (filters.start_date) p.start_date = filters.start_date;
        if (filters.end_date) p.end_date = filters.end_date;
        return p;
    }, [filters]);

    useEffect(() => {
        const controller = new AbortController();
        setLoading(true); setError(''); setRatingRows([]); setObjectiveRows([]); setAnalysis({ sites: [], overall: {} }); setKpis({});
        authAxios.get('/api/results/analysis', { params: apiParams, signal: controller.signal })
            .then(({ data }) => { if (!controller.signal.aborted) { setKpis(data.kpis || {}); setAnalysis(data); setRatingRows(data.ratingRows || []); setObjectiveRows(data.objectiveRows || []); } })
            .catch(() => { if (!controller.signal.aborted) setError('통계를 불러오지 못했습니다. 다시 시도해 주세요.'); })
            .finally(() => { if (!controller.signal.aborted) setLoading(false); });
        return () => controller.abort();
    }, [apiParams, retry]);

    // 차트 옵션(레이아웃 흔들림 방지)
    const chartOptions = useMemo(
        () => ({
        responsive: true,
        maintainAspectRatio: false, // 부모 .chart-wrap 높이에 맞춤
        animation: false,
        plugins: { legend: { position: 'top' }, title: { display: false } },
        }),
        []
    );

    return (
        <div className="survey-result-container">
        {/* 상단 네비 버튼 */}
        <div className="top-right-button-group">
            <button onClick={() => navigate('/dashboard')} className="navigate-button">대시보드</button>
            <button onClick={() => navigate('/user')} className="navigate-button">
            평가단 관리
            </button>
            <button onClick={() => navigate('/site')} className="navigate-button">
            사이트 관리
            </button>
            <button onClick={() => navigate('/admin')} className="navigate-button">
            설문 관리
            </button>
            <button onClick={() => navigate('/responses')} className="navigate-button">
            응답 관리
            </button>
            <button onClick={() => navigate('/results')} className="navigate-button primary">
            통계 보기
            </button>
            <button onClick={() => navigate('/admin/login-notice')} className="navigate-button">로그인 공지 관리</button>
            <button onClick={handleLogout} className="navigate-button logout-button">
            로그아웃
            </button>
        </div>

        {/* 필터 (sticky) */}
        <div className="panel sticky">
            <div className="filters-grid">
            <select
                className='input-beauty' aria-label="사업 선택"
                value={filters.program_id}
                onChange={(e) =>
                setFilters((f) => ({ ...f, program_id: e.target.value, site_id: '', survey_id: '' }))
                }
            >
                <option value="">사업: 전체</option>
                {programs.map((p) => (
                <option key={p.program_id} value={p.program_id}>
                    {p.name} ({p.year})
                </option>
                ))}
            </select>

            <select
                className='input-beauty' aria-label="사이트 선택"
                value={filters.site_id}
                onChange={(e) => setFilters((f) => ({ ...f, site_id: e.target.value, survey_id: '' }))}
            >
                <option value="">사이트: 전체</option>
                {sites.map((s) => (
                <option key={s.site_id} value={s.site_id}>
                    {s.site_name}
                </option>
                ))}
            </select>

            <select
                className='input-beauty' aria-label="설문 선택"
                value={filters.survey_id}
                onChange={(e) => setFilters((f) => ({ ...f, survey_id: e.target.value }))}
            >
                <option value="">설문: 전체</option>
                {surveys.map((sv) => (
                <option key={sv.survey_id} value={sv.survey_id}>
                    {sv.title}
                </option>
                ))}
            </select>

            <select 
                className='input-beauty'
                value={filters.gender} onChange={(e) => setFilters((f) => ({ ...f, gender: e.target.value }))}>
                {genderOptions.map((g) => (
                <option key={g.value} value={g.value}>
                    {g.label}
                </option>
                ))}
            </select>

            <select 
                className='input-beauty'
                value={filters.age} onChange={(e) => setFilters((f) => ({ ...f, age: e.target.value }))}>
                {agePresets.map((a) => (
                <option key={a.value} value={a.value}>
                    {a.label}
                </option>
                ))}
            </select>

            <div className="date-range">
                <input
                className='input-beauty'
                type="date"
                value={filters.start_date}
                onChange={(e) => setFilters((f) => ({ ...f, start_date: e.target.value }))}
                placeholder="시작일"
                />
                <input
                className='input-beauty'
                type="date"
                value={filters.end_date}
                onChange={(e) => setFilters((f) => ({ ...f, end_date: e.target.value }))}
                placeholder="종료일"
                />
                
                <button
                    className="btn"
                    onClick={() =>
                    setFilters((f) => ({ ...f, start_date: today.slice(0, 7) + '-01', end_date: today }))
                    }
                >
                    이번달
                </button>

                  {/* 초기화 버튼 */}
                <button
                    onClick={() => setFilters({
                    program_id: '',
                    site_id: '',
                    survey_id: '',
                    gender: '',
                    age: '',
                    start_date: '',
                    end_date: '',
                    })}
                    className="btn"
                >
                    초기화
                </button>

                <div className="result-export">
                    <button
                        type="button"
                        className="btn result-export__button"
                        onClick={downloadExcel}
                        disabled={exportState === 'loading'}
                        aria-busy={exportState === 'loading'}
                        aria-describedby={exportState !== 'idle' ? 'result-export-feedback' : undefined}
                    >
                        {exportState === 'loading' && <span className="result-export__spinner" aria-hidden="true" />}
                        {exportState === 'loading' ? '엑셀 생성 중…' : '엑셀 다운로드'}
                    </button>
                    {exportState !== 'idle' && (
                        <p id="result-export-feedback"
                           className={`result-export__feedback${exportState === 'error' ? ' result-export__feedback--error' : ''}`}
                           role={exportState === 'error' ? 'alert' : 'status'}>
                            {exportState === 'loading' && '첨부 이미지가 많으면 시간이 걸릴 수 있습니다.'}
                            {exportState === 'success' && '다운로드를 시작했습니다.'}
                            {exportState === 'error' && '엑셀 다운로드에 실패했습니다. 잠시 후 다시 시도해주세요.'}
                        </p>
                    )}
                </div>

            </div>
            </div>
        </div>

        {/* KPI */}
        <div className="section">
            <KpiCards kpis={kpis} loading={loading} />
        </div>

        {error && <p role="alert">{error} <button className="btn" onClick={() => setRetry(value => value + 1)}>다시 불러오기</button></p>}
        <div className="result-stat-note sub-muted">배정·시작·완료는 현재 배정 기준이며, 별점·문항·코멘트는 선택한 제출 기간과 조건을 적용합니다.</div>
        <QuestionNumbers ratingRows={ratingRows} objectiveRows={objectiveRows} loading={loading} />
        {/* 탭 */}
        <div>
            {[
            ['overview', '개요'],
            ['questions', '문항'],
            ['comments', '코멘트'],
            ].map(([key, label]) => (
            <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={activeTab === key ? 'result-button-active' : 'result-button'}
            >
                {label}
            </button>
            ))}
        </div>

        {/* 본문 */}
        <div className="result-container">
            {activeTab === 'overview' && (
            <div className="result-grid">
                {loading ? <p>별점 통계를 불러오는 중입니다.</p> : <RatingOverview analysis={analysis} options={chartOptions} />}
            </div>
            )}

            {activeTab === 'questions' && (
            <div className="result-grid">
                <section className="col-12">
                <QuestionList
                    ratingRows={ratingRows}
                    objectiveRows={objectiveRows}
                    options={chartOptions}
                />
                </section>
            </div>
            )}

            {activeTab === 'comments' && (
            <div className="result-grid">
                <section className="col-12">
                <CommentsTab filters={apiParams} />
                </section>
            </div>
            )}
        </div>
        </div>
    );
    }
